import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JwtAuthService } from 'app/auth/services/jwt-auth.service';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { ErrorsEnum } from '../enums/errors.enum';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    count: number = 0;
    errorPrefix: string = "errors.";

    constructor(
        private router: Router,
        private jwtService: JwtAuthService,
        private translate: TranslateService,
        private snack: MatSnackBar,
        private location: Location) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError(err => {
                console.error('error interceptor', err);

                if ((<string>err.url).includes("PrijavaKorisnickoImeLozinka")) {
                    throw new Error(this.translate.instant('error.ErrLogin'));
                }

                if ((<string>err.url).includes("PrijavaJedinstveniIdentifikator")) {
                    this.jwtService.failedLoginSource.next({
                        failedLogin: true,
                        statusCode: err.status
                    });
                    return of(err);
                }

                if ((<string>err.url).includes("OsveziToken")) {
                    throw new Error(this.translate.instant('errors.isteklaSesija'));
                }

                if (err.status === HttpStatusCode.BadRequest) {
                    return this.handle400Error(err);
                }

                if (err.status === HttpStatusCode.Unauthorized) {
                    return this.handle401Error(err);
                }

                if (err.status === HttpStatusCode.Forbidden) {
                    return this.handle403Error(err);
                }

                if (err.status === HttpStatusCode.NotFound) {
                    return this.handle404Error(err);
                }

                if (err.status === HttpStatusCode.RequestTimeout) {
                    return this.handle408Error(err);
                }

                if (err.status === HttpStatusCode.InternalServerError) {
                    return this.handle500Error(err);
                }

                if (err.status === HttpStatusCode.BadGateway) {
                    return this.handle502Error(err);
                }

                if (err.status === HttpStatusCode.ServiceUnavailable) {
                    return this.handle503Error(err);
                }

                if (err.status === HttpStatusCode.GatewayTimeout) {
                    return this.handle504Error(err);
                }

                if (err.status === 0) {
                    if (this.count == 0) {
                        this.count++;
                        // TODO: smisliti resenje za CORS error kada se zove https://prijava.test.eid.gov.rs/t/carbon.pro/oauth2/token/.well-known/openid-configuration
                        // moguce da je na eID potrebno CORS propustanje za localhost:4200
                        // Za sada je ovaj if dodat da ne bi pri reload-u strane izlazila iritantna greska.
                        if (!err.url?.includes("openid-configuration")) {
                            let msgSession = this.translate.instant(this.errorPrefix + "ErrUnknown");
                            this.snack.open(msgSession, null, { duration: 5000, panelClass: ["errorSnack"] })
                        }
                        this.count = 0;
                    }

                    return of(err);
                }

                if (err.status != HttpStatusCode.NoContent) {
                    return of(err);
                }

                return of(null);
            }));
    }

    private handle400Error(err: HttpErrorResponse): Observable<any> {
        if (!(err.error instanceof Blob)) {
            return this.handle400ErrorFromObject(err.error);
        }
        return new Observable(observer => {
            const reader: FileReader = new FileReader();
            reader.onloadend = () => {
                try {
                    const messageObject = JSON.parse(reader.result as string);
                    this.show400SnackBar(messageObject);
                } catch {
                    this.snack.open(this.translate.instant(this.errorPrefix + "BAD_REQUEST"), null, { duration: 5000, panelClass: ["errorSnack"] });
                    observer.next(null);
                    observer.complete();
                    return;
                }
                observer.next(null);
                observer.complete();
            };
            reader.onerror = () => {
                this.snack.open(this.translate.instant(this.errorPrefix + "BAD_REQUEST"), null, { duration: 5000, panelClass: ["errorSnack"] });
                observer.next(null);
                observer.complete();
            };
            reader.readAsText(err.error);
        });
    }

    private handle400ErrorFromObject(errorBody: any): Observable<any> {
        try {
            this.show400SnackBar(typeof errorBody === 'string' ? JSON.parse(errorBody) : errorBody);
        } catch {
            this.snack.open(this.translate.instant(this.errorPrefix + "BAD_REQUEST"), null, { duration: 5000, panelClass: ["errorSnack"] });
        }
        return of(null);
    }

    private show400SnackBar(messageObject: any): void {
        let validacioneGreske: string[] = [];
        if (messageObject == ErrorsEnum.ERR_MAPIRANJE_OPERACIJA_I_ENDPOINTA ||
            messageObject == ErrorsEnum.NE_POSTOJI_SISTEMSKA_SIFRA ||
            messageObject == ErrorsEnum.NE_POSTOJI_SIFRA_ORGANA) {
            validacioneGreske.push(messageObject);
        } else if (messageObject?.errors) {
            let keys = Object.keys(messageObject.errors);
            keys?.forEach(key => {
                messageObject.errors[key.toString()].forEach(e => {
                    validacioneGreske.push(this.translate.instant(e));
                });
            });
        }
        if (validacioneGreske?.length) {
            this.snack.open(Array.from(new Set(validacioneGreske.map(x => this.translate.instant('validationErrors.' + x))))?.join("\n"), null, { duration: 10000, panelClass: ["errorSnack"] });
        } else {
            this.snack.open(this.translate.instant(this.errorPrefix + "BAD_REQUEST"), null, { duration: 5000, panelClass: ["errorSnack"] });
        }
    }

    private handle401Error(err: HttpErrorResponse): Observable<any> {
        this.jwtService.LogOut();
        this.translate.get(this.errorPrefix + "isteklaSesija").subscribe(msg => {
            this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] });
        });
        this.router.navigate(["session/404"]);
        return of(err);
    }

    private handle403Error(err: HttpErrorResponse): Observable<any> {
        this.location.back();

        let msg = this.translate.instant(this.errorPrefix + "FORBIDDEN");
        this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] })

        return of(err);
    }

    private handle404Error(err: HttpErrorResponse): Observable<any> {
        this.location.back();

        let msg = this.translate.instant(this.errorPrefix + "NOT_FOUND");
        this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] })

        return of(err);
    }

    private handle408Error(err: HttpErrorResponse): Observable<any> {
        this.location.back();

        let msg = this.translate.instant(this.errorPrefix + "REQUEST_TIMEOUT");
        this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] })

        return of(err);
    }

    private handle500Error(err: HttpErrorResponse): Observable<any> {
        this.location.back();

        let msg = this.translate.instant(this.errorPrefix + "internalServerError");
        this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] })

        return of(err);
    }

    private handle502Error(err: HttpErrorResponse): Observable<any> {
        this.location.back();

        let msg = this.translate.instant(this.errorPrefix + "BAD_GATEWAY");
        this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] })

        return of(err);
    }

    private handle503Error(err: HttpErrorResponse): Observable<any> {
        this.location.back();

        let msg = this.translate.instant(this.errorPrefix + "SERVICE_UNAVAILABLE");
        this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] })

        return of(err);
    }

    private handle504Error(err: HttpErrorResponse): Observable<any> {
        this.location.back();

        let msg = this.translate.instant(this.errorPrefix + "GATEWAY_TIMEOUT");
        this.snack.open(msg, null, { duration: 5000, panelClass: ["errorSnack"] })

        return of(err);
    }
}
